import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Form,
  Dropdown,
  Button,
} from "semantic-ui-react";
import CardLayout from "./CardLayout";
import ReactTable from "react-table-6";
import { useIntl } from "react-intl";
import { last } from "lodash";

interface CardPatientSearchProps {
  hnPreData?: string;
  patientList?: any[];
  nationalitiesList?: any[];
  getPatientList?: (data) => void;
  getNationality?: () => void;
  hideCallBack?: () => void;
  onSelectPatient?: (id,hn,full_name,original) => void;
  onOpenModPatientOldName?: (id,hn,full_name) => void;
  onPaginationChange?: (data) => void;
  currentPage?: number;
  hideColumnsByAccessor?: any[];
  hideNationality?: boolean;
  hideCitizenID?: boolean;
  inputChangeAutoSearch?: boolean;
  systemHn8Digit?: boolean;
  formatHn8Digit?: (hnData) => any;
  numberOfPage: number;
  patientListLoading: boolean;
  isAPILoading: boolean;
}

const CardPatientSearch = ({
  hnPreData = '',
  getPatientList = () => {},
  getNationality = () => {},
  hideCallBack = () => {},
  onSelectPatient = () => {},
  nationalitiesList = [],
  patientList = [],
  onOpenModPatientOldName = () => {},
  numberOfPage,
  currentPage = 1,
  onPaginationChange = () => {},
  patientListLoading,
  isAPILoading,
  hideColumnsByAccessor = [],
  hideNationality = false,
  hideCitizenID = false,
  inputChangeAutoSearch = false,
  systemHn8Digit = false,
  formatHn8Digit = () => {}
}: CardPatientSearchProps) => {
  const intl = useIntl();
  const [hn, setHn] = useState("");
  const [fullName, setFullName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [citizenId, setCitizenId] = useState("");
  const [nationality, setNationality] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [patientResult, setPatientResult] = useState<any[]>([]);

  const [clickTimeout, setClickTimeout] = useState<any>(null); // to distinguish between single click and double click
  const [patientSelected, setPatientSelected] = useState<any>(null);

  const styles = {
    mainTable: { height: 420, backgroundColor: "#ffffff", overflowX: "auto" }
  };

  const currentChange = useRef<number>(0)
  const isMounted = useRef(false)

  const handleOnClear = () => {
    setHn("");
    setFullName("");
    setFirstName("");
    setLastName("");
    setCitizenId("");
    setNationality(null);
    setPatientResult([]);
  };

  const handleOnSearch = async (setHn = "") => {
    let hnData = setHn || hn

    if (systemHn8Digit) {
      hnData = formatHn8Digit(hnData)
    }
    if (typeof hnData !== "string") {
      return
    }

    await getPatientList({
      hn: hnData,
      fullName,
      firstName,
      lastName,
      citizenId,
      nationality,
      patientCurrentPage: 1,
    });

    setIsLoading(false)
  };

  useEffect(() => {
    getNationality();
  }, []);

  useEffect(() => {
    setPatientResult(patientList);
  }, [patientList]);

  useEffect(() => {
    isMounted.current = true
    setHn(hnPreData)
    if (hnPreData) {
      handleOnSearch(hnPreData);
    }
  }, [hnPreData])

  useEffect(() => {
    if (isMounted.current && inputChangeAutoSearch && (fullName.length >= 3 || firstName.length >= 3 || lastName.length >= 3) ) {
      currentChange.current += 1
      handleAutoSearch()
    }
  }, [ firstName, lastName])

  const handleAutoSearch = async (hn = "") => {
    const promise = (number: any) => {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve(number);
        }, 500);
      });
    }
    promise(currentChange.current).then((res) => {
      if (currentChange.current === res) {
        handleOnSearch()
      }
    })

  }

  const handleOnChangeHN = (e: any) => {
    setHn(e.target.value);
  };

  // const handleOnChangeFullName = (e: any, data: any) => {
  //   if (patientListLoading) {
  //     return
  //   }
  //   setFullName(data.value);
  // };
    const handleOnChangeFirstName = (e: any, data: any) => {
    if (patientListLoading) {
      return
    }
    setFirstName(data.value);
  };

    const handleOnChangeLastName = (e: any, data: any) => {
    if (patientListLoading) {
      return
    }
    setLastName(data.value);
  };


  const handleOnChangeCitizenID = (e: any) => {
    setCitizenId(e.target.value);
  };

  const handleOnChangeNation = (e: any, { value }: any) => {
    setNationality(value);
  };

  const handleOnSelectPatient = () => {
    if (patientSelected) {
      onSelectPatient(
        (patientSelected).original.id,
        (patientSelected).original.hn,
        (patientSelected).original.full_name,
        (patientSelected).original);
      // hideCallBack();
    }
  };

  const handlePageChange = async (page: any) => {
    setIsLoading(true)
    await onPaginationChange({
      hn: hn,
      firstName,
      lastName,
      citizenId: citizenId,
      nationality: nationality,
      patientCurrentPage: page + 1
    });
    console.log("handlePageChange False")
    setIsLoading(false)
  }

  const handleOnKeyPressHN = async (ev: any) => {
    let hn = ev.target.value.toUpperCase()
    if (ev.key === "Enter") {
      if (inputChangeAutoSearch) {
        handleOnSearch(hn)
      }
    }
  }

  const allGridColumns = useMemo(
    () => [
      {
        Header: "HN",
        accessor: "hn",
        minWidth: 80,
      },
      {
        Header: "Citizen ID / Passport No.",
        accessor: "citizen_passport",
        style: { whiteSpace: "unset" },
        minWidth: 120,
      },
      {
        Header: intl.formatMessage({ id: "ชื่อ นามสกุล(ไทย)" }),
        accessor: "full_name_th",
        style: { whiteSpace: "unset" },
        minWidth: 120,
      },
      {
        Header: intl.formatMessage({ id: "ชื่อ นามสกุล(อังกฤษ)" }),
        accessor: "full_name_en",
        style: { whiteSpace: "unset" },
        minWidth: 120,
      },
      {
        Header: intl.formatMessage({ id: "วัน/เดือน/ปีเกิด" }),
        accessor: "birthdate",
        minWidth: 100,
      },
      {
        Header: intl.formatMessage({ id: "ชื่อมารดา" }),
        accessor: "mother",
        style: { whiteSpace: "unset" },
        minWidth: 120,
      },
      {
        Header: intl.formatMessage({ id: "ที่อยู่" }),
        accessor: "address",
        style: { whiteSpace: "unset" },
        minWidth: 120,
      },
      {
        Header: intl.formatMessage({ id: "Telephone" }),
        accessor: "tel_num",
        minWidth: 100,
      },
      {
        Header: intl.formatMessage({ id: "ชื่อเดิม" }),
        accessor: "old_name",
        style: { whiteSpace: "unset", textAlign: "center" },
        minWidth: 100,
        Cell: (data: any) => {
          if (data.original.old_name) {
            return (
              <Button
                color="blue"
                icon="search"
                onClick={(e) => {
                  e.stopPropagation();

                  onOpenModPatientOldName(
                    data.original.id,
                    data.original.hn,
                    data.original.full_name
                  );
                }}
              />
            );
          }
          return "";
        },
      },
    ],
    [intl.locale]
  );

  const gridColumns = useMemo(() => {
    const columns = [...allGridColumns]
    return columns.filter(col => !(hideColumnsByAccessor || [])?.includes(col.accessor))
  }, [])

  console.log(gridColumns)

  return (
    <CardLayout
      titleText={intl.formatMessage({ id: "ค้นหาผู้ป่วย" })}
      onClose={hideCallBack}
      toggleable={false}
      loading={patientListLoading}
    >
      <Form>
        <Form.Group inline>
          <Form.Field>
            <b>HN</b>
          </Form.Field>
          <Form.Input
            width={2}
            onChange={handleOnChangeHN}
            value={hn}
            onKeyPress={handleOnKeyPressHN}
          />
          <Form.Field>
            <b>{intl.formatMessage({ id: "ชื่อ (ห้ามใส่คำนำหน้า)" })}</b>
          </Form.Field>
          <Form.Input width={4} onChange={handleOnChangeFirstName} value={firstName} />
          <Form.Field>
            <b>{intl.formatMessage({ id: "นามสกุล" })}</b>
          </Form.Field>
          <Form.Input width={4} onChange={handleOnChangeLastName} value={lastName} />
        </Form.Group>
        <Form.Group inline>
          {hideCitizenID ? (
            <Form.Field width={6}></Form.Field>
          ) : (
            <>
              <Form.Field>
                <b>Citizen ID</b>
              </Form.Field>
              <Form.Input width={4} onChange={handleOnChangeCitizenID} value={citizenId} />
            </>
          )}
          {hideNationality ? (
            <Form.Field width={4}></Form.Field>
          ) : (
            <>
              <Form.Field>
                <b>{intl.formatMessage({ id: "สัญชาติ" })}</b>
              </Form.Field>
              <Form.Field width={3}>
                <Dropdown
                  className="block"
                  search
                  selection
                  clearable
                  fluid
                  placeholder="ทุกสัญชาติ"
                  options={nationalitiesList}
                  value={nationality}
                  style={{ width: "100%" }}
                  onChange={handleOnChangeNation}
                />
              </Form.Field>
            </>
          )}

          <Form.Button color="blue" onClick={() => handleOnSearch()}>
            {intl.formatMessage({ id: "ค้นหา" })}
          </Form.Button>
          <Form.Button color="teal" onClick={handleOnClear}>
            Clear
          </Form.Button>
        </Form.Group>
      </Form>

      <ReactTable
        style={styles.mainTable}
        manual
        data={patientResult}
        pageSize={patientList ? (patientList.length < 10 ? 10 : patientList.length) : 10}
        showPageSizeOptions={false}
        page={currentPage - 1}
        pages={numberOfPage}
        onPageChange={handlePageChange}
        loading={isLoading || isAPILoading}
        getNoDataProps={() => {
          return { style: { display: "none" } };
        }}
        getTrProps={(state: any, rowInfo: any) => {
          if (rowInfo && rowInfo.row) {
            let background =
              rowInfo.index === (patientSelected ? patientSelected.index : null)
                ? "#CCE2FF"
                : "white";
            return {
              onClick: (e: any) => {
                setPatientSelected(rowInfo);
                if (clickTimeout !== null) {
                  if ((patientSelected ? patientSelected.index : null) === rowInfo.index) {
                    clearTimeout(clickTimeout);
                    setClickTimeout(null);
                    onSelectPatient(
                      rowInfo.original.id,
                      rowInfo.original.hn,
                      rowInfo.original.full_name,
                      rowInfo.original
                    );
                    // hideCallBack();
                  }
                } else {
                  setClickTimeout(
                    setTimeout(() => {
                      clearTimeout(clickTimeout);
                      setClickTimeout(null);
                    }, 500)
                  );
                }
              },
              style: {
                background: background,
              },
            };
          } else {
            return {};
          }
        }}
        columns={gridColumns}
      ></ReactTable>
      <br />
      <Button color="blue" onClick={handleOnSelectPatient}>
        {intl.formatMessage({ id: "เลือก" })}
      </Button>
    </CardLayout>
  );
};


export default React.memo(CardPatientSearch);
